import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from "@angular/core";
import {
  FormControl,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from "@angular/forms";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { FormlyBootstrapModule } from "@ngx-formly/bootstrap";
import { FormlyModule } from "@ngx-formly/core";
import { Base64 } from "base64-string";
import _ from "lodash";
import { scriptParameterDefault } from "../../../core/constants/script-parameter-default-data";
import { scriptParameterTypes } from "../../../core/constants/script-parameter-types";
import { RestService } from "../../../core/services/rest.service";
import { ToasterService } from "../../../core/services/toaster-service";
import { MaterialModule } from "../../../core/shared/material.module";
import { DynamicFormComponent } from "../../common/dynamic-form/dynamic-form.component";
import { LoadingComponent } from "../../common/loading/loading.component";
import { MessageEnum } from "../../../core/utils/message.enum";

/**
 * A modal component for editing or configuring an item using a dynamic form.
 * This component uses ngx-formly to dynamically render forms based on provided configurations and fields.
 */
@Component({
  selector: "app-editor-item-modal",
  standalone: true,
  imports: [
    CommonModule,
    FormlyModule,
    FormlyBootstrapModule,
    FormsModule,
    ReactiveFormsModule,
    DynamicFormComponent,
    LoadingComponent,
    MaterialModule,
  ],
  templateUrl: "./editor-item-modal.component.html",
  styleUrl: "./editor-item-modal.component.scss",
})
export class EditorItemModalComponent {
  @Input()
  configs: any = {};

  @Input()
  meta: any = {};

  @Input()
  routeParams: any;

  @Input()
  definitionId: string = "";

  @Input()
  paradigm: "Web" | "Mobile" = "Web";

  @Input()
  fields: any = [];

  @Output() fieldChanged = new EventEmitter<{
    field: string;
    value: any;
    modal: any;
  }>();

  transformedFields: any[] = [];

  activeModal = inject(NgbActiveModal);
  dynamicForm: any = new FormGroup({});
  displayLoader = false;
  selectedApplication: any;
  selectedPage: any;
  noItemFoundMessage = "No item found";
  @ViewChild(DynamicFormComponent) dynamicFormComponent!: DynamicFormComponent;
  isActive: boolean = false;
  
  constructor(
    private readonly restService: RestService,
    private toaster: ToasterService
  ) { }

  ngOnInit() {
    this.transformedFields = _.cloneDeep(this.fields);
    this.transformFieldsInput();
    if (this.configs.isEdit) {
      this.dynamicForm.value = this.configs?.model;

      if (this.configs?.model) {
        this.fields = this.fields.map((field: any) => ({
          ...field,
          value: this.configs?.model[field.key] || "",
        }));
      }
      this.displayLoader = true;
      this.setValuesDynamically(0);
    } else {
      // Pre-load file connections for UploadFiles fields on initial open
      this.preloadUploadFilesFields();
    }
  }

  updateFormFields() {
    this.transformedFields = _.cloneDeep(this.fields);
    this.transformFieldsInput();
  }

  addCustomFormFields(field:any) {
    this.transformedFields = _.cloneDeep(this.fields);
    this.transformedFields.push(field);
    this.transformFieldsInput();
  }

  setValuesDynamically(index: number) {
    if (index === this.transformedFields.length) {
      this.displayLoader = false;
      return;
    }
    if (this.transformedFields[index].dataType?.toLowerCase() === "page") {
      let selectedApp = this.transformedFields.find(
        (item: any) => item.dataType.toLowerCase() === "application"
      )?.value;
      this.setPageList(selectedApp, index, true);
    } else if (
      this.transformedFields[index].dataType?.toLowerCase() === "uiobject"
    ) {
      let selectedPage = this.transformedFields.find(
        (item: any) => item.dataType.toLowerCase() === "page"
      )?.value;
      this.setUiObjectList(selectedPage, index, true);
    } else if (
      this.transformedFields[index].dataType?.toLowerCase() === "script"
    ) {
      let selectedFolder = this.transformedFields.find(
        (item: any) => item.dataType.toLowerCase() === "businessfunctionfolder"
      )?.value;
      this.setScriptList(selectedFolder, index, true);
    } else if (
      this.transformedFields[index].dataType?.toLowerCase() === "uploadfiles"
    ) {
      this.setUploadFilesList(index, true);
    } else {
      index++;
      this.setValuesDynamically(index);
    }
  }

  transformFieldsInput() {
    this.transformedFields.forEach((field: any) => {
      field.key = field.id;
      if (field.type === "variableSelect") delete field.labelDescription;
      if (field.options && field.dataType?.toLowerCase() === "string") {
        field.type = "select";
        field.options = field.options.map((item: any) => {
          return {
            label: item,
            value: item,
          };
        });
      } else {
        switch (field?.dataType?.toLowerCase()) {
          case "variable":
            field.type = "text-input";
            if (this.configs?.intellisenseList?.length)
              field.autocompleteOptions = this.configs?.intellisenseList;
            break;
          case "list":
            field.type = "select";
            break;
          case "radio":
            field.type = "radio";
            break;
          case "integer":
            field.type = "number-input";
            field.value = field.value || 0;
            break;
          case "string":
            field.type = "text-input";
            if (this.configs?.intellisenseList?.length)
              field.autocompleteOptions = this.configs?.intellisenseList;
            break;
          case "boolean":
            field.type = "switch";
            field.value = field.value !== undefined ? field.value : true;
            break;
          case "inputparameters":
          case "outputparameters":
            field.type = "input-list";
            break;
          case "uploadfiles":
            field.type = "multi-select";
            field.options = field.options || [];
            field.value = Array.isArray(field.value) ? field.value : [];
            field.isLoading = true;   // spinner shown until files are fetched
            break;
          case "globalvariable":
            field.type = "select";
            field.options = (this.meta?.globalvariable || []).map((item: any) => ({
              ...item,
              label: item.label || item.name || item.displayName,
              value: item.value ?? item.id,
            }));
            break;
        }

        if (!field.type) {
          let type = scriptParameterTypes[field.dataType.toLowerCase()];
          field.type = type || "select";
        }
        if (field.type === "select") {
          field.options =
            (field.options?.length ? field.options : null) ||
            this.meta[field.dataType?.toLowerCase()] ||
            scriptParameterDefault[field.dataType?.toLowerCase()] ||
            [];
          if (
            !this.configs.isEdit &&
            !field.options.length &&
            (field.dataType?.toLowerCase() === "page" ||
              field.dataType?.toLowerCase() === "uiobject" ||
              field.dataType?.toLowerCase() === "script")
          )
            field.isDisabled = true;
        }
      }

      if (!field.isOptional && field.type !== "input-list") {
        field.validations = { required: true };
      }
    });
  }

  onModelChange(params: { field: any; value: string }) {
    this.fieldChanged.emit({ ...params, modal: this.dynamicForm?.value });
    switch (params.field?.dataType?.toLowerCase()) {
      case "application":
        let pageIndex = this.transformedFields.findIndex(
          (field: any) => field.dataType.toLowerCase() === "page"
        );
        if (pageIndex >= 0) {
          this.enableField(pageIndex);
          this.transformedFields[pageIndex].options = [];
          this.setPageList(params.value, pageIndex);
        }
        break;
      case "page":
        let objectIndex = this.transformedFields.findIndex(
          (field: any) => field.dataType.toLowerCase() === "uiobject"
        );
        if (objectIndex >= 0) {
          this.enableField(objectIndex);
          this.transformedFields[objectIndex].options = [];
          this.setUiObjectList(params.value, objectIndex);
        }
        this.parameterizationForPage(params)
        break;
      case "businessfunctionfolder":
        let scriptIndex = this.transformedFields.findIndex(
          (field: any) => field.dataType.toLowerCase() === "script"
        );
        if (scriptIndex >= 0) {
          this.enableField(scriptIndex);
          this.transformedFields[scriptIndex].options = [];
          this.setScriptList(params.value, scriptIndex);
        }
        break;
      case "script":
        this.populateIOParams(params.value);
        break;
      case "uiobject":
        this.parameterizationForUIObjects(params);
        break;
    }
  }

  parameterizationForUIObjects(params: { field: any; value: string }) {
    let uiObject = params.field.options?.find(
      (ele: any) => ele.id === params.value
    );
    if (uiObject?.locators?.length) {
      let locatorIndex = this.transformedFields.findIndex(
        (field: any) => field.dataType.toLowerCase() === "uiobject"
      );

      this.transformedFields[locatorIndex].type="param_ui_object";
      this.transformedFields[locatorIndex].uiParams = this.getDecriptedLocatorVariables(uiObject?.locators);
      if (this.configs?.intellisenseList?.length)
        params.field.autocompleteOptions = this.configs?.intellisenseList;
    }
  }

  parameterizationForPage(params: any) {
    console.log(params);  
    this.meta.page[this.selectedApplication].forEach((ele: any) => {
      if (ele.id === params.value) {
        this.selectedPage = ele;
      }
    });

    const regex = /\$\{([^}]+)\}/g;
    const matches = [...(this.selectedPage?.url?.matchAll(regex) ?? [])].map((m) => m[1]);

    let pageFieldIndex = this.transformedFields.findIndex(
      (field: any) => field.id === "param_page"
    );
    matches.forEach((key) => {
      this.transformedFields[pageFieldIndex].uiParams[key] = "";
    });
    
    if (this.configs?.intellisenseList?.length) params.field.autocompleteOptions = this.configs?.intellisenseList;

  }

  enableField(index: number) {
    this.transformedFields[index].isDisabled = false;
    this.dynamicForm.controls[this.transformedFields[index].key].enable();
    this.transformedFields[index].isLoading = true;
  }

  setPageList(
    applicationId: string,
    index: number,
    isRecursive: boolean = false
  ) {
    if (!isRecursive) this.transformedFields[index].value = null;
    this.transformedFields[index].labelDescription = "";
    this.selectedApplication = applicationId;
    if (this.meta["page"] && this.meta["page"][applicationId]?.length) {
      this.transformedFields[index].options = this.meta["page"][applicationId];
      this.onDataSetCompleted(index, isRecursive);
    } else {
      this.restService
        .getAutomationPage(
          this.routeParams.projectId,
          this.routeParams.automationId,
          applicationId
        )
        .subscribe(
          (res: any) => {
            this.meta.page[applicationId] = [];
            res.data?.forEach((ele: any) => {
              let obj = ele;
              obj.label = ele.name;
              obj.value = ele.id;
              this.meta.page[applicationId].push(obj);
            });
            this.transformedFields[index].options =
              this.meta.page[applicationId];

            this.transformedFields[index].uiParams = this.setUiParamsPage(this.transformedFields[index]);

            if (!this.meta.page[applicationId].length)
              this.transformedFields[index].labelDescription =
                this.noItemFoundMessage;

            if (
              this.configs?.intellisenseList?.length &&
              this.transformedFields[index].uiParams &&
              Object.keys(this.transformedFields[index].uiParams).length > 0
            )
              this.transformedFields[index].autocompleteOptions =
                this.configs?.intellisenseList;

            this.onDataSetCompleted(index, isRecursive);
          },
          () => {
            this.popToaster("", false, true);
          }
        );
    }
  }

  setUiObjectList(pageId: string, index: number, isRecursive: boolean = false) {
    this.transformedFields[index].labelDescription = "";
    if (this.meta["uiObject"] && this.meta["uiObject"][pageId]?.length) {
      this.transformedFields[index].options = this.meta["uiObject"][pageId];
      this.onDataSetCompleted(index, isRecursive);
    } else {
      this.restService
        .getAutomationUiObject(
          this.routeParams.projectId,
          this.routeParams.automationId,
          this.selectedApplication,
          pageId
        )
        .subscribe(
          (res: any) => {
            this.meta.uiObject[pageId] = [];
            res.data?.forEach((ele: any) => {
              let obj = ele;
              obj.label = ele.name;
              obj.value = ele.id;
              this.meta.uiObject[pageId].push(obj);
            });
            this.transformedFields[index].options = this.meta.uiObject[pageId];
            this.transformedFields[index].type="param_ui_object";
            this.transformedFields[index].uiParams =
             this.setUiParams(
              this.transformedFields[index]
            );
            if (
              this.configs?.intellisenseList?.length &&
              this.transformedFields[index].uiParams &&
              Object.keys(this.transformedFields[index].uiParams).length > 0
            )
              this.transformedFields[index].autocompleteOptions =
                this.configs?.intellisenseList;

            // this.clearField(index);
            if (!this.meta.uiObject[pageId].length)
              this.transformedFields[index].labelDescription =
                this.noItemFoundMessage;
            this.onDataSetCompleted(index, isRecursive);
          },
          () => {
            this.popToaster("", false, true);
          }
        );
    }
  }

  setUiParamsPage(app:any){
    const selectedPage = app?.options?.find(
      (item: any) => item.id === app?.value
    );

    if(selectedPage){
      if (app?.uiParams && Object.keys(app?.uiParams)?.length == 0) {
        const regex = /\$\{([^}]+)\}/g;
        const matches = [...(selectedPage?.url?.matchAll(regex) ?? [])].map((m) => m[1]);
        let acc:any 
        matches.forEach((key) => {
          acc[key] = "";
        });
        return acc;
      }else{
        const regex = /\$\{([^}]+)\}/g;
        const matches = [...(selectedPage?.url?.matchAll(regex) ?? [])].map((m) => m[1]);
        let acc:any={};
        matches.forEach((key) => {
          acc[key] = "";
        });
  
        if (app?.uiParams) {
          Object.keys(acc).forEach((key) => {
            if (key in (app?.uiParams || {})) {
              // keep old value if it exists
              acc[key] = app.uiParams[key];
            } else {
              // otherwise set empty
              acc[key] = "";
            }
          });
        }
  
        return acc;
      }
    } else{
      return {};
    }
  

  }

  setUiParams(page: any) {
    const selectedUiObject = page?.options.find(
      (item: any) => item.id === page?.value
    );

    if (page?.uiParams && Object.keys(page?.uiParams)?.length == 0) {
      return this.getDecriptedLocatorVariables(selectedUiObject?.locators);
    }else{
      let selectedUiObjParams = this.getDecriptedLocatorVariables(selectedUiObject?.locators);
      
      if(Object.keys(selectedUiObjParams).length == 0){
        return {};
      }

      const merged: any = {};
     if(selectedUiObjParams && Object.keys(selectedUiObjParams).length>0){
      Object.keys(selectedUiObjParams).forEach((key) => {
        if (key in (page?.uiParams || {})) {
          // keep old value if it exists
          merged[key] = page.uiParams[key];
        } else {
          // otherwise set empty
          merged[key] = "";
        }
      });
     }

      return merged;
    }
  }

  getDecriptedLocatorVariables(locators:any){
    const enc = new Base64();
      return (
        locators?.reduce(
          (acc: Record<string, string>, locator: any) => {
            const regex = /\$\{([^}]+)\}/g;
            let locatorVar = enc.decode(locator?.locator);

            const matches = [...locatorVar.matchAll(regex)].map((m) => m[1]);
            if (matches.length > 0) {
              matches.forEach((match) => {
                acc[match] = "";
              });

              return acc;
            } else {
              return {};
            }
          },
          {}
        ) || {}
      );
  }

  setScriptList(groupId: string, index: number, isRecursive: boolean = false) {
    this.transformedFields[index].labelDescription = "";
    if (this.meta["script"] && this.meta["script"][groupId]?.length) {
      this.transformedFields[index].options = this.meta["script"][groupId];
      if (this.configs.isEdit)
        this.populateIOParams(this.transformedFields[index].value);
      this.onDataSetCompleted(index, isRecursive);
    } else {
      this.restService
        .getAutomationTestScripts(
          this.routeParams.projectId,
          this.routeParams.automationId
        )
        .subscribe(
          (res: any) => {
            this.meta.script[groupId] = [];
            res.data
              ?.filter(
                (item: any) => item.automationScriptGroup?.id === groupId
              )
              ?.forEach((ele: any) => {
                let obj = ele;
                obj.label = ele.name;
                obj.value = ele.id;
                this.meta.script[groupId].push(obj);
              });
            this.transformedFields[index].options = this.meta.script[groupId];
            if (!this.meta.script[groupId].length)
              this.transformedFields[index].labelDescription =
                this.noItemFoundMessage;
            if (this.configs.isEdit)
              this.populateIOParams(this.transformedFields[index].value);
            this.onDataSetCompleted(index, isRecursive);
          },
          () => {
            this.popToaster("", false, true);
          }
        );
    }
  }

  clearField(index: number) {
    setTimeout(() => {
      this.transformedFields[index].value = null;
      this.dynamicForm.controls[this.transformedFields[index].key].setValue(
        null
      );
    }, 50);
  }

  populateIOParams(scriptId: string) {
    let inputParamIndex = this.transformedFields.findIndex(
      (field: any) => field.dataType.toLowerCase() === "inputparameters"
    );
    let outputParamIndex = this.transformedFields.findIndex(
      (field: any) => field.dataType.toLowerCase() === "outputparameters"
    );
    this.transformedFields[inputParamIndex].isLoading = true;
    this.transformedFields[outputParamIndex].isLoading = true;
    this.restService
      .getScriptById(
        this.routeParams.projectId,
        this.routeParams.automationId,
        scriptId
      )
      .subscribe(
        (res: any) => {
          if (res.status === 200) {
            this.transformedFields[inputParamIndex].list = [];
            this.transformedFields[outputParamIndex].list = [];
            this.transformedFields[inputParamIndex].isLoading = false;
            this.transformedFields[outputParamIndex].isLoading = false;
            res.data?.scriptParameters?.forEach((ele: any) => {
              let savedValue = "";
              if (ele.type === "INPUT") {
                savedValue = this.transformedFields[
                  inputParamIndex
                ]?.value?.find((param: any) => param.id === ele.id)?.value;
                this.transformedFields[inputParamIndex].list.push({
                  ...ele,
                  label: ele.name,
                  value: savedValue || ele.value,
                  autocompleteOptions: this.configs.intellisenseList?.length
                    ? this.configs.intellisenseList
                    : undefined,
                });
              } else {
                savedValue = this.transformedFields[
                  outputParamIndex
                ]?.value?.find((param: any) => param.id === ele.id)?.value;
                this.transformedFields[outputParamIndex].list.push({
                  ...ele,
                  label: ele.name,
                  value: savedValue || ele.value,
                  autocompleteOptions: this.configs.intellisenseList?.length
                    ? this.configs.intellisenseList
                    : undefined,
                });
              }
              this.dynamicForm.addControl(
                ele.id,
                new FormControl(savedValue || ele.value, [Validators.required])
              );
            });
          }
        },
        () => {
          this.transformedFields[inputParamIndex].isLoading = false;
          this.transformedFields[outputParamIndex].isLoading = false;
          this.popToaster("", false, true);
        }
      );
  }

  onDataSetCompleted(index: number, isRecursive: boolean = false) {
    this.transformedFields[index].isLoading = false;
    if (isRecursive) {
      index++;
      this.setValuesDynamically(index);
    }
  }

  /**
   * Fetches FILE connections from the backend and populates the options for an
   * UploadFiles multi-select field at the given index.
   */
  setUploadFilesList(index: number, isRecursive: boolean = false) {
    this.transformedFields[index].isLoading = true;
    this.transformedFields[index].labelDescription = '';

    this.restService.getConnections(this.routeParams.projectId).subscribe(
      (res: any) => {
        const fileConns: any[] = res?.data?.FILE ?? [];
        this.transformedFields[index].options = fileConns.map((conn: any) => ({
          label: conn.connectionName,
          value: conn._id ?? conn.id,
        }));
        this.transformedFields[index].isLoading = false;
        if (!fileConns.length) {
          this.transformedFields[index].labelDescription = 'No files found. Upload files via Project Settings → Connections.';
        }
        this.onDataSetCompleted(index, isRecursive);
      },
      () => {
        this.transformedFields[index].isLoading = false;
        this.popToaster('', false, true);
        if (isRecursive) { this.onDataSetCompleted(index, isRecursive); }
      }
    );
  }

  /**
   * Pre-loads file connections for any UploadFiles field when the modal is
   * opened in "add" mode (non-edit), so the spinner resolves immediately.
   */
  preloadUploadFilesFields() {
    this.transformedFields.forEach((field: any, index: number) => {
      if (field.dataType?.toLowerCase() === 'uploadfiles') {
        this.setUploadFilesList(index, false);
      }
    });
  }

  /** Parameterized object fields, such as PERSON-1, must have a value before Add or Update. */
  get missingParameterNames(): string[] {
    const names: string[] = [];
    (this.transformedFields || []).forEach((field: any) => {
      const params = field?.uiParams;
      if (!params || typeof params !== "object") {
        return;
      }
      Object.entries(params).forEach(([key, value]) => {
        if (value == null || !String(value).trim()) {
          names.push(String(key));
        }
      });
    });
    return names;
  }

  get missingParameterMessage(): string {
    const names = this.missingParameterNames;
    if (!names.length) {
      return "";
    }
    const quoted = names.map((name) => `"${name}"`).join(", ");
    return names.length === 1
      ? `Parameter ${quoted} requires a value.`
      : `Parameters ${quoted} require a value.`;
  }

  /**
   * Handles the submission of the dynamic form.
   * Closes the modal and passes the current form controls as the result.
   */
  submit() {
    if (this.missingParameterNames.length) {
      return;
    }
    if (this.definitionId && this.definitionId === "CMD_DEFAULT_CALL") {
      this.transformedFields.forEach((item: any) => {
        if (
          item.dataType === "InputParameters" ||
          item.dataType === "OutputParameters"
        ) {
          item.value = item.list;
          item.value.forEach(
            (ele: any) => (ele.value = this.dynamicForm.value[ele.id])
          );
          delete item.list;
        } else {
          item.value = this.dynamicForm.value[item.id];
          item.displayValue = this.getDisplayValue(item, item.value);
        }
      });
      this.activeModal.close({
        value: this.dynamicForm.value,
        fields: this.transformedFields,
      });
    } else {
      this.transformedFields.forEach((item: any) => {
        item.value = this.dynamicForm.value[item.id];
        item.displayValue = this.getDisplayValue(item, item.value);
      });
      this.activeModal.close({
        value: this.dynamicForm.value,
        fields: this.transformedFields,
      });
    }
  }

  getDisplayValue(item: any, valueId: string) {
    let result: any = "";
    switch (item?.dataType?.toLowerCase()) {
      case "uploadfiles": {
        // valueId is actually an array of selected file IDs; resolve to names
        const ids: string[] = Array.isArray(valueId) ? valueId : [];
        result = ids
          .map((id: string) => item.options?.find((o: any) => o.value === id)?.label ?? id)
          .join(', ');
        break;
      }
      case "application":
        result = this.meta.application?.find(
          (ele: any) => ele.id === valueId
        )?.name;
        break;
      case "businessfunctionfolder":
        result = this.meta.BusinessFunctionFolder?.find(
          (ele: any) => ele.id === valueId
        )?.name;
        break;
      case "page":
        let application = this.transformedFields.find(
          (child: any) => child.dataType.toLowerCase() === "application"
        );
        result = this.meta.page[application.value]?.find(
          (ele: any) => ele.id === valueId
        )?.name;
        break;
      case "uiobject":
        let page = this.transformedFields.find(
          (child: any) => child.dataType.toLowerCase() === "page"
        );
        result = this.meta.uiObject[page.value]?.find(
          (ele: any) => ele.id === valueId
        )?.name;
        break;
      case "script":
        let folder = this.transformedFields.find(
          (child: any) =>
            child.dataType.toLowerCase() === "businessfunctionfolder"
        );
        result = this.meta.script[folder.value]?.find(
          (ele: any) => ele.id === valueId
        )?.name;
        break;
      case "globalvariable": {
        const match =
          item.options?.find(
            (ele: any) =>
              String(ele.value ?? ele.id) === String(valueId)
          ) ||
          this.meta?.globalvariable?.find(
            (ele: any) =>
              String(ele.value ?? ele.id) === String(valueId)
          );
        result =
          match?.label || match?.name || match?.displayName || "";
        break;
      }
    }
    return result;
  }

  /**
   * Displays a toaster notification.
   * @param message The message to display.
   * @param isSuccess Whether the notification indicates success.
   * @param showDefaultError Whether to display a default error message.
   */
  popToaster(message: string, isSuccess = true, showDefaultError?: boolean) {
    if (isSuccess) {
      this.toaster.pop({ type: "success", text: message });
    } else {
      this.toaster.pop({
        type: "error",
        text: showDefaultError ? MessageEnum.SOMETHING_WENT_WRONG : message,
      });
    }
  }
}
